import React, { useContext, useEffect } from 'react';
import { FieldContext, OptionsContext } from '../../hooks/Contexts';
import OptionControls from './OptionControls';
import MetaContent from '../metadata/MetaContent';
import { useForm, useFormState } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Errors from '../common/Errors';
import { fetchOption, handleUpdateOption } from './constants/handleOption';
import {
  createMC,
  destroyMC,
  updateMC,
  contentsToUpdate,
} from '../metadata/constants/handleMetaContent';
import { notify } from '../../helpers/notifications';

const OptionEdit = ({ option, contentsByEntry, toggleEdit, editing }) => {
  const { field } = useContext(FieldContext);
  const { options, refreshOption } = useContext(OptionsContext);
  const showMeta = field.type != 'checkbox' && contentsByEntry.length > 0;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    criteriaMode: 'all',
  });
  const { dirtyFields } = useFormState({ control });

  useEffect(() => {
    if (document.activeElement.name == 'name') {
      setValue('value', watch('name'), { shouldDirty: true });
      if (errors['value']) {
        clearErrors('value');
      }
    }
  }, [watch('name')]);

  const findMetaContentId = (meID) => {
    return contentsByEntry.find((ec) => ec[0].id == meID)[1].id;
  };

  const handleOptionFetch = async (id) => {
    let reloaded = await fetchOption(id);
    if (reloaded.ok) refreshOption(options, id, reloaded.data);
  };

  const onSubmit = async (data) => {
    const { name, value, local_quota, ...mc } = data;
    const updatedOption = { name, value, local_quota };
    let promises = [];

    // handle each metadata content update
    contentsToUpdate(mc, contentsByEntry).forEach(
      ({ action, metadata_entry_id, description }) => {
        if (action) {
          let mcID = findMetaContentId(metadata_entry_id) || null;
          switch (action) {
            case 'create':
              return promises.push(
                createMC(metadata_entry_id, description, option.id.toString())
              );
            case 'destroy':
              return promises.push(destroyMC(mcID));
            case 'update':
              return promises.push(updateMC(mcID, description));
            default:
              break;
          }
        }
      }
    );

    // ensure metadata updates, then update option to pull in all updated metadata_contents
    Promise.all(promises).then(async (metaResponses) => {
      // notify for each metadata_content that was changed
      metaResponses.forEach((res) => {
        if (res.ok) {
          notify.success('Metadata Saved', { showOnlyTheLastOne: false });
        } else {
          notify.failure('Metadata Failed to Save', {
            showOnlyTheLastOne: false,
          });
        }
      });

      // handle option update if fields or dirty, or fetch if only metadata was changed
      if (dirtyFields.name || dirtyFields.value || dirtyFields.local_quota) {
        let updated = await handleUpdateOption(option.id, updatedOption);
        if (updated.ok) {
          refreshOption(options, option.id, updated.data);
        } else if (updated.errors) {
          // still need to reload the option in case metadata was changed
          if (metaResponses.length) handleOptionFetch(option.id);
          return Object.entries(updated.errors).forEach(([key, message]) =>
            setError(key, { types: { [key]: message } })
          );
        }
      } else if (metaResponses.length) {
        handleOptionFetch(option.id);
      }
      toggleEdit();
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='cargo'>
        <div className='name'>
          <div className='content'>
            <strong>Name:</strong>
            <input
              {...register('name', {
                value: option.name,
                required: 'Required',
              })}
            />
            <ErrorMessage
              errors={errors}
              name='name'
              render={({ messages }) => (
                <Errors isSpan={true} messages={messages} />
              )}
            />
          </div>

          <OptionControls
            field={field}
            option={option}
            editing={editing}
            toggleEdit={toggleEdit}
            onSubmit={onSubmit}
          />
        </div>

        <div className='meta val'>
          <strong>Value:</strong>
          <input
            {...register('value', {
              value: option.value,
              required: 'Required',
            })}
          />
          <ErrorMessage
            errors={errors}
            name='value'
            render={({ messages }) => (
              <Errors isSpan={true} messages={messages} />
            )}
          />
        </div>

        {field.informs_global && (
          <div className={'meta'}>
            <strong>Option Quota:</strong>
            <input
              className='local-quota'
              type='number'
              min={0}
              {...register('local_quota', {
                value: option.local_quota,
                setValueAs: (v) => Number(v),
                shouldUnregister: true,
              })}
            />
            <ErrorMessage
              errors={errors}
              name='local_quota'
              render={({ messages }) => (
                <Errors isSpan={true} messages={messages} />
              )}
            />
          </div>
        )}

        {showMeta && (
          <MetaContent
            option={option}
            editing={editing}
            entryContent={contentsByEntry}
            register={register}
          />
        )}
      </div>
    </form>
  );
};

export default OptionEdit;
